import React, { useEffect, useState } from 'react';
import { getRateCardPercentage } from '../../api';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { formatNumber } from '../../lib/format';
import { cx } from '../../ui';
import { PLANNING_ASSUMPTIONS, marginOfError, requiredInvites } from '../feasibility/feasibility';
import { describeAssumptions } from '../feasibility/FeasibilityNote';
import type { FeasibilityState } from '../feasibility/useFeasibility';

const PREF_KEY = 'responsily.planningStripCollapsed';
const readCollapsed = () => {
  try { return window.localStorage.getItem(PREF_KEY) === '1'; } catch (e) { return false; }
};
const writeCollapsed = (v: boolean) => {
  try { window.localStorage.setItem(PREF_KEY, v ? '1' : '0'); } catch (e) { /* preference is optional */ }
};

const num = (v: unknown) => (/^\d+$/.test(String(v ?? '').trim()) ? Number(v) : null);

function Metric({ label, title, children, className }: { label: string; title?: string; children?: React.ReactNode; className?: string }) {
  return (
    <div className={cx('min-w-0 px-4 py-2', className)} title={title}>
      <p className="truncate text-[11px] text-muted">{label}</p>
      <p className="text-[15px] font-bold text-ink">{children}</p>
    </div>
  );
}

/** The form values or the saved settings: only these three fields are read. */
interface PlanningValues {
  noOfWantedCompletes?: unknown;
  estimatedIncidenceRate?: unknown;
  estimatedLenghthOfInterview?: unknown;
}

interface PlanningStripProps {
  values: PlanningValues | null | undefined;
  /** the server's feasibility answer for the wizard's current values (see useFeasibility) */
  feasibility?: FeasibilityState;
}

/**
 * Live planning figures for a target group, as a slim strip above the wizard step (collapsible, remembered).
 * Nothing here is fabricated:
 *  - feasible completes comes from the SERVER, which counts real panelists matching the target group and applies its
 *    own response / drop-out settings; when it cannot (no country yet, missing data) the strip says so, never 0;
 *  - invitations / margin of error are calculated here from the form values, using the server's response and
 *    drop-out rates when it has reported them;
 *  - the rate-card figure is the backend's percentage, shown as an index, not a currency price.
 * Shows 5 figures on wide windows, 3 on medium ones, and a one-line summary on small ones.
 */
export function PlanningStrip({ values, feasibility }: PlanningStripProps) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggle = () => setCollapsed((c) => { writeCollapsed(!c); return !c; });

  const completes = num(values && values.noOfWantedCompletes);
  const ir = num(values && values.estimatedIncidenceRate);
  const loi = num(values && values.estimatedLenghthOfInterview);

  const server = feasibility && feasibility.data;
  const responseRate = server && server.assumptions ? server.assumptions.responseRate : PLANNING_ASSUMPTIONS.responseRate;
  const dropoutRate = server && server.assumptions ? server.assumptions.dropOutRate : PLANNING_ASSUMPTIONS.dropoutRate;
  const invites = requiredInvites({ completes, ir, responseRate, dropoutRate });
  const moe = marginOfError(completes);

  const query = useDebouncedValue(ir && loi ? `${ir}|${loi}` : '', 400);
  const [rate, setRate] = useState<{ state: 'idle' | 'loading' | 'ok' | 'error'; value: number | null }>({ state: 'idle', value: null });
  useEffect(() => {
    if (!query) { setRate({ state: 'idle', value: null }); return undefined; }
    const [qIr, qLoi] = query.split('|');
    let cancelled = false;
    setRate((r) => ({ ...r, state: 'loading' }));
    getRateCardPercentage(qIr, qLoi)
      .then((v) => { if (!cancelled) setRate({ state: 'ok', value: v }); })
      .catch(() => { if (!cancelled) setRate({ state: 'error', value: null }); });
    return () => { cancelled = true; };
  }, [query]);

  const muted = (text: string) => <span className="text-[13px] font-normal text-muted">{text}</span>;
  let feasibleCell: React.ReactNode;
  let feasibleTitle = 'Counted from real panelists by the server';
  if (feasibility && feasibility.error) {
    feasibleCell = muted('Unavailable');
    feasibleTitle = 'The feasibility check could not be completed';
  } else if (!server) {
    feasibleCell = feasibility && (feasibility.loading || feasibility.updating) ? '…' : muted('Not available');
    feasibleTitle = 'Choose a country in Basics to count the panel';
  } else if (server.feasibleCompletes === null || server.status === 'notEnoughData') {
    feasibleCell = muted('Not enough data');
    feasibleTitle = server.notEnoughData.join('; ') || 'Not enough data to estimate';
  } else {
    feasibleCell = (
      <span className={cx(server.feasible ? 'text-ok' : 'text-danger', feasibility && feasibility.updating && 'opacity-60')}>
        {formatNumber(server.feasibleCompletes)} of {formatNumber(server.wantedCompletes)} <span aria-hidden="true">{server.feasible ? '✓' : '✗'}</span>
        <span className="sr-only">{server.feasible ? ' feasible' : ' not feasible'}</span>
      </span>
    );
    feasibleTitle = `${formatNumber(server.panelSize)} matching panelists, ${describeAssumptions(server)}. A planning estimate from the panel as it is today.`;
  }

  return (
    <section
      aria-label="Planning estimate"
      className="mb-4 flex items-stretch rounded-xl border border-line bg-surface shadow-card"
      title="Planning estimates, not measured results. Final feasibility depends on live panel availability."
    >
      <div className="flex w-24 flex-none items-center border-r border-line px-3 py-2 text-[11px] font-bold uppercase leading-tight tracking-wider text-muted">Planning estimate</div>

      {!collapsed && (
        <div className="hidden min-w-0 flex-1 grid-cols-3 divide-x divide-line md:grid min-[1180px]:grid-cols-5">
          <Metric label="Wanted completes">{formatNumber(completes)}</Metric>
          <Metric label="Invitations needed" title={`Assumes ${Math.round(responseRate * 1000) / 10}% response and ${Math.round(dropoutRate * 1000) / 10}% drop-out`}>{invites === null ? '—' : formatNumber(invites)}</Metric>
          <Metric label="Margin of error" title="95% confidence, worst case">{moe === null ? '—' : `±${moe.toFixed(1)}%`}</Metric>
          <Metric label="Feasible completes" title={feasibleTitle} className="hidden min-[1180px]:block">{feasibleCell}</Metric>
          <Metric label="Rate-card index" title="Percentage from the rate card, not a price" className="hidden min-[1180px]:block">
            {rate.state === 'loading' ? '…' : rate.state === 'error' ? muted('Unavailable') : rate.value === null ? '—' : `${rate.value}%`}
          </Metric>
        </div>
      )}

      {/* One-line summary: always when collapsed, and on small windows where the figures do not fit */}
      <p className={cx('min-w-0 flex-1 items-center px-4 py-2 text-sm text-ink', collapsed ? 'flex' : 'flex md:hidden')}>
        <span className="truncate"><b>{formatNumber(completes)}</b> wanted · <b>{invites === null ? '—' : formatNumber(invites)}</b> invitations · {moe === null ? '—' : `±${moe.toFixed(1)}%`}</span>
      </p>

      <button type="button" onClick={toggle} aria-expanded={!collapsed} className="flex-none rounded-r-xl px-4 text-xs font-medium text-muted hover:bg-canvas hover:text-ink">
        {collapsed ? 'Show ▾' : 'Hide ▴'}
      </button>
    </section>
  );
}
